import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface FeatureModeToggleProps {
  mode: 'demo' | 'live';
  disabled?: boolean;
  loading?: boolean;
  onChange: (mode: 'demo' | 'live') => void;
  className?: string;
}

export const FeatureModeToggle: React.FC<FeatureModeToggleProps> = ({
  mode,
  disabled = false,
  loading = false,
  onChange,
  className,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleToggle = () => {
    if (disabled || loading) return;
    
    setIsAnimating(true);
    const newMode = mode === 'demo' ? 'live' : 'demo';
    onChange(newMode);
    
    setTimeout(() => setIsAnimating(false), 200);
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={mode === 'live'}
      aria-label={`Switch to ${mode === 'demo' ? 'live' : 'demo'} mode`}
      disabled={disabled || loading}
      onClick={handleToggle}
      className={cn(
        'relative inline-flex h-8 w-[120px] items-center rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2',
        mode === 'live' 
          ? 'bg-green-500 focus:ring-green-500' 
          : 'bg-gray-400 focus:ring-gray-400',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && !loading && 'cursor-pointer hover:opacity-90',
        isAnimating && 'scale-[0.98]',
        className
      )}
    >
      {/* Background labels */}
      <span className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium text-white pointer-events-none">
        <span className={cn(
          'transition-opacity duration-200',
          mode === 'demo' ? 'opacity-100' : 'opacity-0'
        )}>
          Demo
        </span>
        <span className={cn(
          'transition-opacity duration-200',
          mode === 'live' ? 'opacity-100' : 'opacity-0'
        )}>
          Live
        </span>
      </span>

      {/* Sliding indicator */}
      <span
        className={cn(
          'absolute top-1 h-6 w-[56px] rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out flex items-center justify-center',
          mode === 'live' ? 'translate-x-[60px]' : 'translate-x-1'
        )}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-gray-600" />
        ) : (
          <span className={cn(
            'text-xs font-semibold',
            mode === 'live' ? 'text-green-600' : 'text-gray-600'
          )}>
            {mode === 'demo' ? 'Demo' : 'Live'}
          </span>
        )}
      </span>
    </button>
  );
};
